// Drives the same look as ProcessingOverlay for a fabricated "Or open a sample"
// pick from CaptureModal. Everything here is a local setInterval stepper — no
// fetchJob/fetchPlan/uploadFiles call, no jobId, no real Ingest Queue row.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { markSampleOpened } from './sampleReviewState';

const STEP_MS = 900;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface SampleDoc {
  key: string;
  file: string;
  desc: string;
  size: string;
  sha: string;
  type: string;
  typeConf: number;
  entity: string;
  entityConf: number;
  risk: string;
  nItems: number;
  warningCount: number;
  indexOnly: boolean;
  chunks?: number;
}

interface SampleOverlayProps {
  sample: SampleDoc;
  onClose: () => void;
}

const extOf = (filename: string) => {
  const dot = String(filename).lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toUpperCase().slice(0, 4) : 'FILE';
};

const OUTCOME_INDEXED = 'Filed and indexed — no approval needed.';
const OUTCOME_REVIEW = 'Ready for review in the Ingest Queue.';

interface StageDef {
  name: string;
  out: string;
}

function buildStages(sample: SampleDoc): StageDef[] {
  const shaOut = `sha256 ${sample.sha.slice(0, 12)}…`;
  const typeOut = `${sample.type.toLowerCase()} · ${sample.typeConf}%`;

  if (sample.indexOnly) {
    return [
      { name: 'Fingerprint & dedupe', out: shaOut },
      { name: 'Classify document', out: typeOut },
      { name: 'Chunk & embed', out: `${sample.chunks} chunks · titan-v2` },
      { name: 'File to contract shelf', out: 'no approval needed' },
    ];
  }

  return [
    { name: 'Fingerprint & dedupe', out: shaOut },
    { name: 'Classify document', out: typeOut },
    { name: 'Extract fields', out: '' },
    { name: 'Check against contract', out: sample.warningCount === 0 ? 'no issues flagged' : `${sample.warningCount} issue${sample.warningCount === 1 ? '' : 's'} flagged` },
    { name: 'Build mutation plan', out: `${sample.nItems} proposed change${sample.nItems === 1 ? '' : 's'}` },
  ];
}

export default function SampleOverlay({ sample, onClose }: SampleOverlayProps) {
  const stageDefs = buildStages(sample);
  const [done, setDone] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);

  const settled = done >= stageDefs.length;
  const classified = done >= 2;

  useEffect(() => {
    const interval = setInterval(() => {
      setDone((d) => {
        const next = d + 1;
        if (next >= stageDefs.length) {
          clearInterval(interval);
        }
        return next;
      });
    }, STEP_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const node = dialogRef.current;
    const first = node?.querySelector(FOCUSABLE);
    (first || node)?.focus();
    return () => { previouslyFocused.current?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKey = useCallback((e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => {
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const els = Array.from(node.querySelectorAll(FOCUSABLE));
      if (!els.length) return;
      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    window.addEventListener('keydown', onTab);
    return () => window.removeEventListener('keydown', onTab);
  }, []);

  const outcome = sample.indexOnly ? OUTCOME_INDEXED : OUTCOME_REVIEW;

  return createPortal(
    <div className="ingest-modal-overlay bi-console" onClick={onClose} role="presentation">
      <div
        className="ingest-proc"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Sample document preview"
      >
        <div className="ing-scan-head">
          <span className="shield" aria-hidden="true">⛨</span>
          Scanning for hacks
        </div>

        <div className="ingest-proc-top">
          <div className={`ingest-scanner${!settled ? ' scanning' : ''}`} aria-hidden="true">
            <div className="ingest-scanner-beam" />
            {[12, 24, 36, 48, 60, 72, 84, 96, 108, 120].map((y) => (
              <div key={y} className="ingest-scanner-ln" style={{ top: y, width: y % 3 === 0 ? 48 : 72 }} />
            ))}
            <div className={`ingest-scanner-hl${classified ? ' on' : ''}`} style={{ top: 24, width: 62 }} />
            <div className={`ingest-scanner-hl${done >= 3 ? ' on' : ''}`} style={{ top: 60, width: 44 }} />
            <div className={`ingest-scanner-hl${settled ? ' on' : ''}`} style={{ top: 96, width: 70 }} />
          </div>

          <div className="ingest-proc-info">
            <div className="ingest-proc-fn" title={sample.file}>{sample.file}</div>
            <div className="ingest-proc-sub">
              {[sample.size, extOf(sample.file), 'uploaded just now'].filter(Boolean).join(' · ')}
            </div>

            {classified ? (
              <div className="ingest-verdict">
                <div className="ingest-verdict-t">
                  <span className="ingest-verdict-name">{sample.type}</span>
                  <span className={`ingest-chip ingest-chip-${sample.risk}`}>{sample.risk}</span>
                </div>
                <div className="ingest-verdict-row">
                  <span>type confidence</span><span>{sample.typeConf}%</span>
                </div>
                <div className="ingest-verdict-row">
                  <span>target entity</span>
                  <span>{sample.entity} · {sample.entityConf}%</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="ingest-stages">
          {stageDefs.map((s, i) => {
            const state = i < done ? 'done' : i === done && !settled ? 'run' : 'todo';
            return (
              <div key={s.name} className={`ingest-stage ${state}`}>
                <span className="ingest-stage-dot" aria-hidden="true">{state === 'done' ? '✓' : ''}</span>
                <span className="ingest-stage-name">{s.name}</span>
                <span className="ingest-stage-out">{state === 'done' ? s.out : ''}</span>
              </div>
            );
          })}
        </div>

        <div className="ingest-proc-foot">
          <span className="ingest-proc-note" data-testid="ingest-proc-note" aria-live="polite">
            {settled ? outcome : 'Nothing is written until you approve it.'}
          </span>
          <button
            className="btn btn--primary"
            onClick={() => {
              if (settled) {
                markSampleOpened(sample.key);
                window.dispatchEvent(new Event('pb:sample-opened'));
                (window as any).showPage?.('ingest');
              }
              onClose();
            }}
          >
            {settled ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
